import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShoppingCart, Plus, ChevronDown, X, Eye, Truck, Loader2, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import { OBRAS, STATUS_PEDIDO, StatusBadge } from './insumos.utils';

function PedidoFormModal({ onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ fornecedor_id: '', obra: '', data_pedido: '', previsao_entrega: '', observacoes: '' });
  const [itens, setItens] = useState([{ material_id: '', descricao_livre: '', quantidade_pedida: '', unidade_id: '', preco_unitario: '' }]);
  const [erro, setErro] = useState('');

  const { data: fornecedores } = useQuery({ queryKey: ['insumos-forn'], queryFn: () => api.get('/insumos/fornecedores').then(r => r.data.data) });
  const { data: materiais } = useQuery({ queryKey: ['insumos-materiais-all'], queryFn: () => api.get('/insumos/materiais', { params: { limit: 500 } }).then(r => r.data.data) });
  const { data: unidades } = useQuery({ queryKey: ['insumos-units'], queryFn: () => api.get('/insumos/unidades').then(r => r.data.data) });

  const save = useMutation({
    mutationFn: () => api.post('/insumos/pedidos', { ...form, itens: itens.filter(i => i.quantidade_pedida) }),
    onSuccess: () => { qc.invalidateQueries(['insumos-pedidos']); onClose(); },
    onError: (e) => setErro(e.response?.data?.error || 'Erro ao criar pedido'),
  });

  const setItem = (idx, k, v) => setItens(its => its.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addItem = () => setItens(its => [...its, { material_id: '', descricao_livre: '', quantidade_pedida: '', unidade_id: '', preco_unitario: '' }]);
  const delItem = (idx) => setItens(its => its.filter((_, i) => i !== idx));

  const total = itens.reduce((a, i) => a + (parseFloat(i.quantidade_pedida || 0) * parseFloat(i.preco_unitario || 0)), 0);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-800 text-lg">Novo Pedido de Compra</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        {erro && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{erro}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Fornecedor *</label>
            <select value={form.fornecedor_id} onChange={e => setForm(f => ({ ...f, fornecedor_id: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar...</option>
              {(fornecedores || []).map(f => <option key={f.id} value={f.id}>{f.razao_social}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Obra Destino *</label>
            <select value={form.obra} onChange={e => setForm(f => ({ ...f, obra: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar...</option>
              {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data do Pedido</label>
            <input type="date" value={form.data_pedido} onChange={e => setForm(f => ({ ...f, data_pedido: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Previsão de Entrega</label>
            <input type="date" value={form.previsao_entrega} onChange={e => setForm(f => ({ ...f, previsao_entrega: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Observações</label>
            <input value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </div>

        {/* Itens */}
        <div className="border rounded-xl overflow-hidden mb-4">
          <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600">ITENS DO PEDIDO</span>
            <button onClick={addItem} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Plus size={12} /> Adicionar Item</button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left">Material (catálogo)</th>
                <th className="px-3 py-2 text-left">Descrição Livre</th>
                <th className="px-3 py-2 text-right w-24">Qtd</th>
                <th className="px-3 py-2 text-left w-24">Unid.</th>
                <th className="px-3 py-2 text-right w-28">Preço Unit.</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item, idx) => (
                <tr key={idx} className="border-b border-gray-50">
                  <td className="px-3 py-2">
                    <select value={item.material_id} onChange={e => setItem(idx, 'material_id', e.target.value)}
                      className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                      <option value="">Não catalogado</option>
                      {(materiais || []).map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input value={item.descricao_livre} onChange={e => setItem(idx, 'descricao_livre', e.target.value)}
                      placeholder="Descrição..." className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" value={item.quantidade_pedida} onChange={e => setItem(idx, 'quantidade_pedida', e.target.value)}
                      className="w-full text-right border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </td>
                  <td className="px-3 py-2">
                    <select value={item.unidade_id} onChange={e => setItem(idx, 'unidade_id', e.target.value)}
                      className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                      <option value="">—</option>
                      {(unidades || []).map(u => <option key={u.id} value={u.id}>{u.sigla}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" step="0.01" value={item.preco_unitario} onChange={e => setItem(idx, 'preco_unitario', e.target.value)}
                      className="w-full text-right border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => delItem(idx)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > 0 && <div className="px-4 py-2 border-t text-right text-sm font-semibold text-gray-700">Total: R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>}
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.fornecedor_id || !form.obra}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {save.isPending ? 'Criando...' : 'Criar Pedido'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InsumosPedidos() {
  const [statusFiltro, setStatusFiltro] = useState('');
  const [obraFiltro, setObraFiltro] = useState('');
  const [modalNovo, setModalNovo] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['insumos-pedidos', statusFiltro, obraFiltro],
    queryFn: () => api.get('/insumos/pedidos', { params: { status: statusFiltro || undefined, obra: obraFiltro || undefined, limit: 50 } }).then(r => r.data),
  });

  const pedidos = data?.data || [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShoppingCart size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-800">Pedidos de Compra</h1>
        </div>
        <button onClick={() => setModalNovo(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          <Plus size={16} /> Novo Pedido
        </button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap">
        <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
          className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_PEDIDO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
          className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Todas as obras</option>
          {OBRAS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 border-b">
            <tr>
              <th className="px-4 py-3 text-left">Pedido</th>
              <th className="px-4 py-3 text-left">Fornecedor</th>
              <th className="px-4 py-3 text-left">Obra</th>
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3 text-left">Previsão</th>
              <th className="px-4 py-3 text-right">Valor</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400"><Loader2 className="animate-spin mx-auto" /></td></tr>
            ) : pedidos.length ? pedidos.map(p => (
              <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-blue-600 font-medium">{p.numero_pedido}</td>
                <td className="px-4 py-3 text-gray-700">{p.fornecedor_nome || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{p.obra}</td>
                <td className="px-4 py-3 text-gray-500">{p.data_pedido ? format(parseISO(p.data_pedido), 'dd/MM/yy') : '—'}</td>
                <td className="px-4 py-3">
                  {p.previsao_entrega ? (
                    <span className={`text-xs ${new Date(p.previsao_entrega) < new Date() && p.status === 'aguardando' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {format(parseISO(p.previsao_entrega), 'dd/MM/yy')}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono text-gray-700">
                  {p.valor_total ? `R$ ${parseFloat(p.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
                </td>
                <td className="px-4 py-3 text-center"><StatusBadge value={p.status} map={STATUS_PEDIDO} /></td>
              </tr>
            )) : (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Nenhum pedido encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalNovo && <PedidoFormModal onClose={() => setModalNovo(false)} />}
    </div>
  );
}
